import React, { useEffect, useState, useRef, useCallback } from "react";

// =============================================================
// Triple-Wheel Data Engine adapted for a 3D rotating ring.
//
//   W3 (Staging)   ← API / seed generator  (async fetch, fills once)
//   W2 (Buffer)    ← pulled from W3        (ready-to-use queue)
//   W1 (Visible)   ← pulled from W2        (what the ring shows)
//
// SMART BEHAVIOR:
// - If DB has ≤ 6 images → fetch once, show all, no recycling needed
// - If DB has > 6 images → progressive feed, recycle from pool
// - Never re-fetches images already in the pool
// =============================================================

const TOTAL_DATABASE_RECORDS = 6;

/** Simulated API – replace with real fetch when ready. */
const fetchGalleryBatch = async (cursor, limit = 18) => {
  await new Promise((r) => setTimeout(r, 400));

  let fetchCursor = ((cursor - 1) % TOTAL_DATABASE_RECORDS) + 1;
  if (fetchCursor <= 0) fetchCursor += TOTAL_DATABASE_RECORDS;

  const data = [];
  for (let i = 0; i < limit; i++) {
    const id = ((fetchCursor + i - 1) % TOTAL_DATABASE_RECORDS) + 1;
    data.push({
      id,
      url: `/images/profile${id}.jpg`,
    });
  }
  return data;
};

// ─── Component ────────────────────────────────────────────────

const RingGallery = ({
  images: externalImages,
  imageCount = 18,
  autoRotate = true,
  speed = 0.3,
  colors,
  onCardClick,
}) => {
  // ── Ring layout constants ──
  const MAX_VISIBLE = 6;
  const MAX_ITEM = 240;
  const MIN_RADIUS = 190;
  const MAX_RADIUS = 520;

  // ── Triple-wheel limits ──
  const BATCH_SIZE = Math.min(18, TOTAL_DATABASE_RECORDS); // Never fetch more than DB has

  // ── Refs ──
  const containerRef = useRef(null);
  const rotationRef = useRef(0);
  const velocityRef = useRef(0);
  const isDraggingRef = useRef(false);
  const activePointer = useRef(null);
  const lastXRef = useRef(0);
  const rafRef = useRef(null);
  const cursorRef = useRef(1);
  const isFetchingRef = useRef(false);
  const lastFeedStep = useRef(0);
  const mountedRef = useRef(true);

  // ── Pool: all unique images we've ever fetched ──
  const poolRef = useRef([]); // Master pool (deduplicated)
  const poolIndexRef = useRef(0); // Next index to pull from pool for recycling
  const allFetchedRef = useRef(false); // True once we have all DB records

  // ── Renderable state ──
  const [radius, setRadius] = useState(280);
  const [ringItems, setRingItems] = useState([]); // W1 – visible cards
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(0); // Actual card count (≤ MAX_VISIBLE)

  // ────────────────────────────────────────────────
  // FETCH → POOL  (fetch only unique, stop when all loaded)
  // ────────────────────────────────────────────────
  const fetchIntoPool = useCallback(async () => {
    // Already have everything? Don't call API again.
    if (allFetchedRef.current) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    try {
      const batch = await fetchGalleryBatch(cursorRef.current, BATCH_SIZE);
      if (!mountedRef.current) return;

      // Deduplicate: only add items not already in the pool
      const existingIds = new Set(poolRef.current.map((item) => item.id));
      let newCount = 0;
      for (const item of batch) {
        if (!existingIds.has(item.id)) {
          poolRef.current.push(item);
          existingIds.add(item.id);
          newCount++;
        }
      }

      cursorRef.current =
        ((cursorRef.current + BATCH_SIZE - 1) % TOTAL_DATABASE_RECORDS) + 1;

      // Check: have we collected all unique records?
      if (poolRef.current.length >= TOTAL_DATABASE_RECORDS || newCount === 0) {
        allFetchedRef.current = true;
      }
    } finally {
      isFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ────────────────────────────────────────────────
  // GET NEXT ITEM from pool (cycles through for recycling)
  // ────────────────────────────────────────────────
  const getNextFromPool = useCallback(() => {
    const pool = poolRef.current;
    if (pool.length === 0) return null;

    const item = pool[poolIndexRef.current % pool.length];
    poolIndexRef.current++;
    return item;
  }, []);

  // ────────────────────────────────────────────────
  // FEED NEXT ITEM into ring (W1)
  // Only needed when DB > MAX_VISIBLE (ring cycles new images in)
  // ────────────────────────────────────────────────
  const feedNextItem = useCallback(() => {
    // If we have ≤ MAX_VISIBLE images total, ring is static — no feeding needed
    if (poolRef.current.length <= MAX_VISIBLE) return;

    // If pool not fully loaded yet, try fetching more
    if (!allFetchedRef.current) {
      fetchIntoPool();
    }

    const newItem = getNextFromPool();
    if (!newItem) return;

    const currentVisible = Math.min(MAX_VISIBLE, poolRef.current.length);
    const currentAngle = 360 / currentVisible;

    setRingItems((prev) => {
      const next = [...prev];
      const backSlot =
        Math.floor(
          (((-rotationRef.current % 360) + 360) % 360) / currentAngle,
        ) % currentVisible;
      const replaceSlot =
        (backSlot + Math.floor(currentVisible / 2)) % currentVisible;

      if (next.length < currentVisible) {
        next.push(newItem);
      } else {
        next[replaceSlot] = newItem;
      }
      return next;
    });
  }, [fetchIntoPool, getNextFromPool]);

  // ────────────────────────────────────────────────
  // INIT – bootstrap
  // ────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    const bootstrap = async () => {
      // Fetch until we have enough for the ring (or everything)
      await fetchIntoPool();
      if (!allFetchedRef.current && poolRef.current.length < MAX_VISIBLE) {
        await fetchIntoPool();
      }

      const uniqueCount = poolRef.current.length;
      const count = Math.min(MAX_VISIBLE, uniqueCount);
      setVisibleCount(count);

      // Seed W1 with initial items
      const initial = poolRef.current.slice(0, count);
      poolIndexRef.current = count; // Start recycling after these
      setRingItems(initial);
      setIsLoading(false);
    };

    bootstrap();

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ────────────────────────────────────────────────
  // RESIZE observer – responsive radius (adapts for fewer cards)
  // ────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      const count = visibleCount || MAX_VISIBLE;

      if (count > 5) {
        // User's explicitly requested logic for 6+ items
        const item = Math.min(w * 0.4, MAX_ITEM);
        const r = Math.min(MAX_RADIUS, (count * item) / (2 * Math.PI));
        setRadius(Math.max(MIN_RADIUS, r));
      } else {
        // Hardcoded ideal radii for small quantities exactly as requested
        let optimalRadius = 0;
        if (count === 2) optimalRadius = 100;
        else if (count === 3) optimalRadius = 120;
        else if (count === 4) optimalRadius = 140;
        else if (count === 5) optimalRadius = 160;

        const maxAllowed = w * 0.45;
        setRadius(Math.min(optimalRadius, Math.max(maxAllowed, 100)));
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [visibleCount]);

  // ────────────────────────────────────────────────
  // ANIMATION LOOP
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (visibleCount === 0) return;

    const stepAngle = 360 / visibleCount;

    const tick = () => {
      let rot = rotationRef.current;

      if (!isDraggingRef.current) {
        if (autoRotate && Math.abs(velocityRef.current) < 0.05) {
          rot -= speed * 0.4;
        } else {
          velocityRef.current *= 0.94;
          if (Math.abs(velocityRef.current) > 0.05) {
            rot += velocityRef.current * 0.3;
          }
        }
      }

      rotationRef.current = rot;
      setRotation(rot);

      // Feed check — only when DB has more than MAX_VISIBLE images
      if (poolRef.current.length > MAX_VISIBLE) {
        const currentStep = Math.floor(rot / stepAngle);
        if (currentStep !== lastFeedStep.current) {
          lastFeedStep.current = currentStep;
          feedNextItem();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [autoRotate, speed, feedNextItem, visibleCount]);

  // ────────────────────────────────────────────────
  // POINTER HANDLERS (drag)
  // ────────────────────────────────────────────────
  const onPointerDown = (e) => {
    activePointer.current = e.pointerId;
    e.target.setPointerCapture(e.pointerId);
    isDraggingRef.current = true;
    lastXRef.current = e.clientX;
    velocityRef.current = 0;
  };

  const onPointerMove = (e) => {
    if (!isDraggingRef.current || activePointer.current !== e.pointerId) return;
    if (e.cancelable) e.preventDefault();

    const delta = e.clientX - lastXRef.current;
    rotationRef.current += delta * 0.3;
    velocityRef.current = delta;
    lastXRef.current = e.clientX;
  };

  const onPointerUp = (e) => {
    if (activePointer.current === e.pointerId) {
      activePointer.current = null;
      isDraggingRef.current = false;
    }
  };

  // ────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────
  const angle = visibleCount > 0 ? 360 / visibleCount : 60;

  return (
    <div className="w-full h-full" style={{ perspective: 2000 }}>
      <div
        ref={containerRef}
        className="relative w-full h-full"
        style={{ transformStyle: "preserve-3d", touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: "3px solid rgba(255,255,255,0.1)",
                borderTopColor: "#3b82f6",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {ringItems.map((item, i) => {
          if (!item) return null;
          const rot = i * angle + rotation;
          return (
            <FlipCard
              key={`ring-${item.id}-${i}`}
              item={item}
              rot={rot}
              radius={radius}
              onCardClick={onCardClick}
            />
          );
        })}
      </div>
    </div>
  );
};

// ─── FlipCard ─────────────────────────────────────────────────
// Starts showing the image. Flips to card-back on click, clicks again to flip back.
const CARD_SIZE = 200;

const FlipCard = ({ item, rot, radius, onCardClick }) => {
  const [flipped, setFlipped] = React.useState(false);

  // Reset flip state when a new image is fed into this slot
  React.useEffect(() => {
    setFlipped(false);
  }, [item.id]);

  // Auto flip back after 4 seconds
  React.useEffect(() => {
    let timer;
    if (flipped) {
      timer = setTimeout(() => {
        setFlipped(false);
        if (onCardClick) onCardClick(null);
      }, 4000);
    }
    return () => clearTimeout(timer);
  }, [flipped, onCardClick]);

  const handleClick = (e) => {
    // Don't flip if user was dragging
    if (e.defaultPrevented) return;
    const nextFlipped = !flipped;
    setFlipped(nextFlipped);
    if (onCardClick) {
      onCardClick(nextFlipped ? item.id : null);
    }
  };

  const faceStyle = {
    position: "absolute",
    inset: 0,
    borderRadius: "1rem",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    overflow: "hidden",
  };

  return (
    <div
      onClick={handleClick}
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: CARD_SIZE,
        height: CARD_SIZE,
        marginLeft: -CARD_SIZE / 2,
        marginTop: -CARD_SIZE / 2,
        transform: `rotateY(${rot}deg) translateZ(${radius}px)`,
        perspective: 600,
        cursor: "pointer",
      }}
    >
      {/* Inner wrapper – rotates on flip */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
          // false = back face (0deg), true = image (180deg)
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          borderRadius: "1rem",
          boxShadow: "0 20px 40px rgba(0,0,0,0.35)",
        }}
      >
        {/* ── FRONT face – the image (default, flipped=false, 0deg) ── */}
        <div
          style={{
            ...faceStyle,
            background: "#111827",
          }}
        >
          <img
            src={item.url}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
          {/* ID badge */}
          {/* <div
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              background: "rgba(37,99,235,0.9)",
              color: "#fff",
              padding: "3px 10px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 800,
              fontFamily: "monospace",
              boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            #{item.id}
          </div> */}
        </div>

        {/* ── BACK face – decorative card back (shown when flipped=true, rotateY(180deg)) ── */}
        <div
          style={{
            ...faceStyle,
            transform: "rotateY(180deg)",
            background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "80%",
              height: "80%",
              borderRadius: "0.5rem",
              border: "2px solid rgba(59,130,246,0.4)",
              background:
                "repeating-linear-gradient(45deg, rgba(59,130,246,0.05) 0px, rgba(59,130,246,0.05) 2px, transparent 2px, transparent 10px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(59,130,246,0.5)"
              strokeWidth="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="12" cy="12" r="3" />
              <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
            </svg>
            {/* <span
              style={{
                color: "rgba(59,130,246,0.6)",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "monospace",
              }}
            >
              Info #{item.id}
            </span> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RingGallery;
